import { designVariables } from "@studio3t/design-variables";
import {
  ButtonSmallSecondary,
  Icon,
  IconSVG,
  StyledTableScroll,
  styledTableClassNames,
} from "@studio3t/generic-components";
import {
  type Column,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type Row,
  type SortingState,
  type Table as TableInstance,
  useReactTable,
} from "@tanstack/react-table";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import { JobStatusBadge } from "@/components/JobStatusBadge";
import { PipelineFlowCell } from "@/components/PipelineFlowCell";
import { SkeletonBar, SkeletonPill } from "@/components/Skeleton";
import { SortableHeader as SharedSortableHeader } from "@/components/SortableHeader";
import { useJobs } from "@/hooks/useApi";
import type { JobMetadata } from "@/types/api";
import { userStorage } from "@/utils/userStorage";
import { CardHeader, CardTitle, EmptyMessage, GUTTER } from "./dashboardCards";
import { deriveJobControlState } from "./pipelineControls";
import {
  formatPipeline,
  normalizeState,
  sortByStatusPriority,
} from "./pipelineHelpers";

const SINGLE_LINE = designVariables.type.singleLine;
const SEMANTIC = designVariables.ui.semantic;
const TABLE = designVariables.ui.table;

// The five most recently started pipelines (Figma shows five rows).
const RECENT_COUNT = 5;

const TableGutter = styled.div`
  padding: 0 ${GUTTER}px ${GUTTER}px;
`;

// With nothing deployed there is no table to frame: the card keeps the
// mock's body height (Figma 16102-26437: 240px under the header) with the
// one line at its top, so the hero's shape does not jump as the first
// pipeline arrives.
const EMPTY_BODY_HEIGHT = 240;

const EmptyBody = styled.div`
  box-sizing: border-box;
  height: ${EMPTY_BODY_HEIGHT}px;
`;

// The design outlines the table on all four sides (border-t/b on the table
// node plus the wrap's 1px side edges); interior lines come from the cells.
const TableFrame = styled.div`
  border: 1px solid ${(props) => props.theme.tableBackground};

  /* StyledTableScroll draws its own frame border and stretches to 100%
   * height; this frame owns the border here, and the scroll should hug
   * the five rows. */
  .${styledTableClassNames.scroll} {
    border: none;
    height: auto;
    min-height: 0;
  }

  /* StyledTableScroll sets overscroll-behavior: none on its viewport, which
     swallows the wheel even here, where the five rows never scroll — with
     the pointer over the table the dashboard would not scroll at all. Let
     the wheel chain to the page (same as the service detail's table). */
  [data-overlayscrollbars-viewport] {
    overscroll-behavior: auto !important;
  }
`;

const Table = styled.table`
  width: 100%;
  /* Below this the card scrolls horizontally instead of squashing the
     Pipeline flow column into unreadability: the three fixed columns plus
     the room a three-stage chain needs before its sink is cut off
     (TTTL-186). */
  min-width: 960px;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
  background: ${(props) => props.theme.tableTheadCellBackground};

  tbody tr:last-child td {
    border-bottom: none;
  }

  th:last-child,
  td:last-child {
    border-right: none;
  }
`;

const TableHeader = styled.th`
  text-align: left;
  height: ${TABLE.thead.cell.height}px;
  padding: 0 ${TABLE.thead.cell.paddingr}px 0 ${TABLE.thead.cell.paddingl}px;
  font-size: ${SINGLE_LINE.small.regular.fontSize}px;
  font-weight: ${SINGLE_LINE.small.regular.fontWeight};
  color: ${(props) => props.theme.semanticTextLight};
  white-space: nowrap;
  border-bottom: 1px solid ${(props) => props.theme.tableBackground};
  border-right: 1px solid ${(props) => props.theme.tableBackground};
`;

const TableRow = styled.tr`
  cursor: pointer;

  &:hover {
    background: ${(props) => props.theme.tableTbodyCellHoverBackground};
  }

  /* Rows are keyboard-focusable (Enter/Space opens the pipeline). The
     browser-default focus ring is kept; only inset it so the scroll
     container doesn't clip it. */
  &:focus-visible {
    outline-offset: -${SEMANTIC.focusstroke}px;
  }
`;

const TableCell = styled.td`
  height: ${TABLE.tbody.cell.heightm}px;
  padding: 0 ${TABLE.tbody.cell.padding}px;
  font-size: ${SINGLE_LINE.default.regular.fontSize}px;
  color: ${(props) => props.theme.semanticTextBody};
  vertical-align: middle;
  border-bottom: 1px solid ${(props) => props.theme.tableBackground};
  border-right: 1px solid ${(props) => props.theme.tableBackground};
  overflow: hidden;
`;

const CellText = styled.span`
  display: block;
  padding: 0 ${TABLE.tbody.cell.gap}px;
  color: ${(props) => props.theme.semanticTextBody};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Status badges hug their content rather than stretching to the cell width.
const StatusSlot = styled.div`
  padding-left: ${TABLE.tbody.cell.gap}px;

  & > * {
    display: inline-flex;
  }
`;

const EmptyRowMessage = styled.div`
  padding: ${GUTTER}px 0;
  text-align: center;
  font-size: ${SINGLE_LINE.small.regular.fontSize}px;
  font-style: italic;
  color: ${(props) => props.theme.semanticTextLight};
`;

// Adapts a TanStack column to the shared SortableHeader control.
function SortableHeader({
  column,
  children,
}: {
  column: Column<JobMetadata, unknown>;
  children: ReactNode;
}) {
  return (
    <SharedSortableHeader
      direction={column.getIsSorted() || null}
      onClick={() => column.toggleSorting()}
    >
      {children}
    </SharedSortableHeader>
  );
}

// Mirrors the column's sort state onto the <th> so assistive tech can announce
// whether (and how) a column is sorted.
function ariaSortFor(
  column: Column<JobMetadata, unknown>,
): "ascending" | "descending" | "none" {
  const sorted = column.getIsSorted();
  if (sorted === "asc") return "ascending";
  if (sorted === "desc") return "descending";
  return "none";
}

// The sort choice survives a page refresh (per user).
const SETTINGS_KEY = "dashboard-recent-pipelines";

const SORTABLE_COLUMN_IDS = ["name", "pipeline", "status", "checkpoint"];

// Guards against stale storage referencing other columns.
function parseSort(value: unknown): SortingState | undefined {
  if (!Array.isArray(value)) return undefined;
  const sort = value.filter(
    (s): s is { id: string; desc: boolean } =>
      typeof s === "object" &&
      s !== null &&
      typeof (s as { id?: unknown }).id === "string" &&
      SORTABLE_COLUMN_IDS.includes((s as { id: string }).id) &&
      typeof (s as { desc?: unknown }).desc === "boolean",
  );
  return sort.length ? sort : undefined;
}

function loadStoredSort(): SortingState {
  try {
    const raw = userStorage.getItem(SETTINGS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return [];
    return parseSort((parsed as Record<string, unknown>).sort) ?? [];
  } catch {
    return [];
  }
}

const columnHelper = createColumnHelper<JobMetadata>();

const columns = [
  columnHelper.accessor("name", {
    header: "Name",
    cell: (info) => <CellText>{info.getValue()}</CellText>,
  }),
  // Sortable by source (service.resource) only — the flow's natural sort key,
  // same as the Pipelines list.
  columnHelper.accessor((job) => formatPipeline(job).source, {
    id: "pipeline",
    header: "Pipeline",
    cell: (info) => <PipelineFlowCell job={info.row.original} />,
  }),
  columnHelper.accessor(
    (job) =>
      deriveJobControlState(
        normalizeState(job.state),
        normalizeState(job.desired_state),
      ).displayState,
    {
      id: "status",
      header: "Status",
      sortingFn: sortByStatusPriority,
      cell: (info) => (
        <StatusSlot>
          <JobStatusBadge state={info.getValue()} />
        </StatusSlot>
      ),
    },
  ),
  columnHelper.accessor(
    (job) => (job.pipeline?.checkpoint?.enabled ? "Enabled" : "Off"),
    {
      id: "checkpoint",
      header: "Checkpoint",
      cell: (info) => <CellText>{info.getValue()}</CellText>,
    },
  ),
];

const SKELETON_ROWS = Array.from(
  { length: RECENT_COUNT },
  (_, i) => `skeleton-${i}`,
);

function SkeletonBodyRows() {
  return (
    <>
      {SKELETON_ROWS.map((key) => (
        <TableRow key={key} aria-hidden="true">
          <TableCell>
            <SkeletonBar $width="70%" />
          </TableCell>
          <TableCell>
            <SkeletonBar $width="55%" />
          </TableCell>
          <TableCell>
            <SkeletonPill $width="64px" />
          </TableCell>
          <TableCell>
            <SkeletonBar $width="60%" />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

// Every column here is sortable, so each header cell wraps the shared control
// and mirrors its state onto the <th>.
function TableHead({ table }: { table: TableInstance<JobMetadata> }) {
  return (
    <thead>
      {table.getHeaderGroups().map((headerGroup) => (
        <tr key={headerGroup.id}>
          {headerGroup.headers.map((header) => (
            <TableHeader key={header.id} aria-sort={ariaSortFor(header.column)}>
              <SortableHeader column={header.column}>
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
              </SortableHeader>
            </TableHeader>
          ))}
        </tr>
      ))}
    </thead>
  );
}

// A single body row: opens the pipeline on click/Enter/Space.
function PipelineTableRow({
  row,
  onRowClick,
}: {
  row: Row<JobMetadata>;
  onRowClick: (name: string) => void;
}) {
  return (
    <TableRow
      tabIndex={0}
      onClick={() => onRowClick(row.original.name)}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onRowClick(row.original.name);
        }
      }}
    >
      {row.getVisibleCells().map((cell) => (
        <TableCell key={cell.id}>
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </TableCell>
      ))}
    </TableRow>
  );
}

// Skeleton rows while the jobs query loads, the sorted rows once it lands.
function TableBody({
  table,
  loading,
  onRowClick,
}: {
  table: TableInstance<JobMetadata>;
  loading: boolean;
  onRowClick: (name: string) => void;
}) {
  return (
    <tbody>
      {loading && <SkeletonBodyRows />}
      {!loading &&
        table
          .getRowModel()
          .rows.map((row) => (
            <PipelineTableRow key={row.id} row={row} onRowClick={onRowClick} />
          ))}
    </tbody>
  );
}

// The framed table.
function RecentPipelinesTable({
  table,
  loading,
  onRowClick,
}: {
  table: TableInstance<JobMetadata>;
  loading: boolean;
  onRowClick: (name: string) => void;
}) {
  return (
    <TableFrame>
      <StyledTableScroll>
        <Table>
          {/* 240/120 mirror Figma's Table cell "middle width"/"min width"
            variables, which @studio3t/design-variables doesn't export
            yet. */}
          <colgroup>
            <col style={{ width: 240 }} />
            <col />
            <col style={{ width: 120 }} />
            <col style={{ width: 120 }} />
          </colgroup>
          <TableHead table={table} />
          <TableBody table={table} loading={loading} onRowClick={onRowClick} />
        </Table>
      </StyledTableScroll>
    </TableFrame>
  );
}

export function DashboardRecentPipelines() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { data: jobs, isLoading, error } = useJobs();
  const [sorting, setSorting] = useState<SortingState>(loadStoredSort);

  // "Recent" = the most recently started pipelines; the user's column sort
  // then orders within that subset.
  const recentJobs = useMemo(() => {
    if (!jobs) return [];
    const startedAt = (job: JobMetadata) => {
      const t = new Date(job.started_at).getTime();
      return Number.isNaN(t) ? 0 : t;
    };
    return [...jobs]
      .sort((a, b) => startedAt(b) - startedAt(a))
      .slice(0, RECENT_COUNT);
  }, [jobs]);

  const table = useReactTable({
    data: recentJobs,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (job) => job.name,
  });

  useEffect(() => {
    userStorage.setItem(SETTINGS_KEY, JSON.stringify({ sort: sorting }));
  }, [sorting]);

  const openPipeline = (name: string) => {
    navigate(`/pipelines/${encodeURIComponent(name)}`);
  };

  const loading = isLoading && !jobs;
  const empty = !loading && !error && recentJobs.length === 0;

  return (
    <>
      <CardHeader>
        <IconSVG
          icon={Icon.Trace}
          size={20}
          colors={{ default: theme.semanticTextHeading }}
        />
        <CardTitle>Recent pipelines</CardTitle>
        {/* Nothing to view all of while nothing is deployed. */}
        {!empty && (
          <ButtonSmallSecondary
            text="View all"
            onClick={() => navigate("/pipelines")}
          />
        )}
      </CardHeader>

      {empty ? (
        <EmptyBody>
          <EmptyMessage>You have not deployed any pipeline yet</EmptyMessage>
        </EmptyBody>
      ) : (
        <TableGutter>
          {error ? (
            <EmptyRowMessage>Failed to load pipelines</EmptyRowMessage>
          ) : (
            <RecentPipelinesTable
              table={table}
              loading={loading}
              onRowClick={openPipeline}
            />
          )}
        </TableGutter>
      )}
    </>
  );
}
